import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { matchesFilters } from '../map/filters';
import { apiUrl } from '../api';

/**
 * A newer build is live if /version.json (written at build time, never
 * cached) names a different version than this bundle. Phones in particular
 * like to resurrect a tab from cache, so we check rather than trust a refresh.
 */
function useNewerVersion(): string | null {
  const [newer, setNewer] = useState<string | null>(null);
  useEffect(() => {
    const check = () =>
      fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((v: { version?: string } | null) => {
          if (v?.version && v.version !== __APP_VERSION__) setNewer(v.version);
        })
        .catch(() => undefined);
    void check();
    const t = setInterval(check, 5 * 60_000);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
  return newer;
}

function useServerVersion(): string | null {
  const [v, setV] = useState<string | null>(null);
  useEffect(() => {
    fetch(apiUrl('/api/health'))
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { version?: string } | null) => setV(d?.version ?? null))
      .catch(() => undefined);
  }, []);
  return v;
}

const CONNECTION_LABEL: Record<string, string> = {
  connected: 'Live',
  connecting: 'Connecting',
  reconnecting: 'Reconnecting',
  disconnected: 'Offline',
};

export default function StatusBar() {
  const sources = useStore((s) => s.sources);
  const aircraft = useStore((s) => s.aircraft);
  const filters = useStore((s) => s.filters);
  const lastUpdateTs = useStore((s) => s.lastUpdateTs);
  const connection = useStore((s) => s.connection);
  const [, tick] = useState(0);
  const newerVersion = useNewerVersion();
  const serverVersion = useServerVersion();

  // Same matchesFilters used to build the rendered plane set in MapView, so
  // this count never disagrees with what's actually drawn on the map.
  const visibleCount = useMemo(
    () => [...aircraft.values()].filter((ac) => matchesFilters(ac, filters)).length,
    [aircraft, filters],
  );
  const totalCount = aircraft.size;

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const age = lastUpdateTs ? Math.max(0, Math.round((Date.now() - lastUpdateTs) / 1000)) : null;
  const stale = age != null && age > 15;

  return (
    <footer className="status-bar">
      <span className={`status-conn status-conn-${connection}`} title={`WebSocket: ${connection}`}>
        <span className="status-dot" aria-hidden="true" />
        {CONNECTION_LABEL[connection] ?? connection}
      </span>
      <span className="status-count">
        <span className="mono">{visibleCount.toLocaleString()}</span>
        {visibleCount === totalCount ? ' aircraft' : (
          <>
            {' of '}
            <span className="mono">{totalCount.toLocaleString()}</span> aircraft
          </>
        )}
      </span>
      <span className={`status-age${stale ? ' is-stale' : ''}`}>
        {age != null ? (
          <>
            Updated <span className="mono">{age}s</span> ago
          </>
        ) : (
          'Waiting for data'
        )}
      </span>
      {newerVersion ? (
        <button className="status-update" onClick={() => location.reload()} title={`Version ${newerVersion} is available`}>
          Update to v{newerVersion}
        </button>
      ) : (
        <span
          className="status-version mono"
          title={`Site v${__APP_VERSION__}, built ${new Date(__BUILD_TIME__).toLocaleString()}${
            serverVersion ? ` · server v${serverVersion}` : ''
          }`}
        >
          v{__APP_VERSION__}
        </span>
      )}
      <span className="status-sources" aria-label="Data sources">
        {sources.map((s) => (
          <span
            key={s.name}
            className={`status-source${s.ok ? ' is-ok' : ' is-err'}`}
            title={
              s.ok
                ? `${s.name}: OK, ${s.lastAircraftCount} aircraft last poll`
                : `${s.name}: ${s.lastError ?? 'error'} (${s.consecutiveFailures}× failed)`
            }
          >
            <span className="status-dot" aria-hidden="true" />
            {s.name}
            {!s.ok && <span className="sr-only"> (failing)</span>}
          </span>
        ))}
      </span>
    </footer>
  );
}
